/**
 * S8 Register Service
 * Manages Schedule 8 controlled drug register entries.
 * All mutations are dual-witness, audit-logged, and balance-reconciled.
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  createS8RegisterEntry,
  createS8DrugSummary,
  validateS8RegisterEntry,
  S8_TRANSACTION_TYPE,
} from '../../data/models/S8Register';

const LOG_PREFIX = '[S8RegisterService]';

class S8RegisterService {
  constructor() {
    this.entriesKey = 'agedcare_s8_register';
    this.summaryKey = 'agedcare_s8_summary';
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  async getAll() {
    try {
      const entries = LocalStorageService.get(this.entriesKey, []);
      console.debug(`${LOG_PREFIX} getAll → ${entries.length} entries`);
      return { success: true, data: entries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getAll failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getByDrug(drugName) {
    try {
      const entries = LocalStorageService.find(
        this.entriesKey,
        (e) => e.drugName === drugName,
      );
      return { success: true, data: entries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getByDrug failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getByResident(residentId) {
    try {
      const entries = LocalStorageService.find(
        this.entriesKey,
        (e) => e.residentId === residentId,
      );
      return { success: true, data: entries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getByResident failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getById(id) {
    try {
      const entry = LocalStorageService.findById(this.entriesKey, id);
      if (!entry) return { success: false, error: 'S8 register entry not found' };
      return { success: true, data: entry };
    } catch (error) {
      console.error(`${LOG_PREFIX} getById failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getDrugSummaries() {
    try {
      const summaries = LocalStorageService.get(this.summaryKey, []);
      return { success: true, data: summaries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getDrugSummaries failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getDiscrepancies() {
    try {
      const entries = LocalStorageService.find(
        this.entriesKey,
        (e) => e.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY,
      );
      return { success: true, data: entries };
    } catch (error) {
      console.error(`${LOG_PREFIX} getDiscrepancies failed:`, error);
      return { success: false, error: error.message };
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  async recordTransaction(entryData) {
    try {
      const currentBalance = this._getCurrentBalance(entryData.drugName, entryData.strength);

      const direction = {
        [S8_TRANSACTION_TYPE.RECEIVED]: 1,
        [S8_TRANSACTION_TYPE.TRANSFERRED_IN]: 1,
        [S8_TRANSACTION_TYPE.ADMINISTERED]: -1,
        [S8_TRANSACTION_TYPE.WASTED]: -1,
        [S8_TRANSACTION_TYPE.RETURNED]: -1,
        [S8_TRANSACTION_TYPE.TRANSFERRED_OUT]: -1,
        [S8_TRANSACTION_TYPE.DISCREPANCY]: 0,
        [S8_TRANSACTION_TYPE.STOCK_CHECK]: 0,
      };

      const delta = (direction[entryData.transactionType] || 0) * (entryData.quantity || 0);
      const balanceAfter = entryData.transactionType === S8_TRANSACTION_TYPE.STOCK_CHECK
        ? entryData.quantity
        : currentBalance + delta;

      const entry = createS8RegisterEntry({
        ...entryData,
        balanceBefore: currentBalance,
        balanceAfter,
      });

      const validation = validateS8RegisterEntry(entry);
      if (!validation.isValid) {
        console.warn(`${LOG_PREFIX} Validation failed:`, validation.errors);
        return { success: false, errors: validation.errors };
      }

      const created = LocalStorageService.addItem(this.entriesKey, entry);

      this._updateDrugSummary(created);

      LocalStorageService.logAudit({
        action: 'S8_TRANSACTION',
        action_type: 'CREATE',
        record_type: 'S8Register',
        record_id: created.id,
        new_value: created,
        user_id: entryData.performedBy || 'system',
        metadata: {
          transactionType: entry.transactionType,
          drugName: entry.drugName,
          quantity: entry.quantity,
          balanceBefore: entry.balanceBefore,
          balanceAfter: entry.balanceAfter,
          witnessedBy: entry.witnessedBy,
        },
      });

      console.debug(`${LOG_PREFIX} Transaction recorded: ${entry.transactionType} ${entry.drugName} qty=${entry.quantity} balance=${entry.balanceAfter}`);
      return { success: true, data: created };
    } catch (error) {
      console.error(`${LOG_PREFIX} recordTransaction failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async recordStockCheck(drugName, strength, actualCount, performedBy, performedByName, witnessedBy, witnessedByName, notes = '') {
    const currentBalance = this._getCurrentBalance(drugName, strength);
    const hasVariance = actualCount !== currentBalance;

    const entryData = {
      drugName,
      strength,
      transactionType: hasVariance ? S8_TRANSACTION_TYPE.DISCREPANCY : S8_TRANSACTION_TYPE.STOCK_CHECK,
      quantity: actualCount,
      balanceBefore: currentBalance,
      balanceAfter: actualCount,
      performedBy,
      performedByName,
      witnessedBy,
      witnessedByName,
      notes: hasVariance
        ? `Stock check variance: expected ${currentBalance}, actual ${actualCount}. ${notes}`
        : `Stock check confirmed: ${actualCount} on hand. ${notes}`,
    };

    const entry = createS8RegisterEntry(entryData);
    // Stock checks bypass normal balance validation
    const created = LocalStorageService.addItem(this.entriesKey, entry);
    this._updateDrugSummary(created);

    LocalStorageService.logAudit({
      action: 'S8_STOCK_CHECK',
      action_type: 'CREATE',
      record_type: 'S8Register',
      record_id: created.id,
      new_value: created,
      user_id: performedBy,
      metadata: { hasVariance, expectedBalance: currentBalance, actualCount },
    });

    console.debug(`${LOG_PREFIX} Stock check: ${drugName} expected=${currentBalance} actual=${actualCount} variance=${hasVariance}`);
    return { success: true, data: created, hasVariance };
  }

  // ---------------------------------------------------------------------------
  // Helpers (private)
  // ---------------------------------------------------------------------------

  _getCurrentBalance(drugName, strength) {
    const entries = LocalStorageService.find(
      this.entriesKey,
      (e) => e.drugName === drugName && (!strength || e.strength === strength),
    );
    if (entries.length === 0) return 0;
    const sorted = entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return sorted[0].balanceAfter ?? 0;
  }

  _updateDrugSummary(entry) {
    const summaries = LocalStorageService.get(this.summaryKey, []);
    const key = `${entry.drugName}|${entry.strength}`;
    const idx = summaries.findIndex((s) => `${s.drugName}|${s.strength}` === key);

    const summaryData = {
      drugName: entry.drugName,
      genericName: entry.genericName,
      strength: entry.strength,
      form: entry.form,
      currentBalance: entry.balanceAfter,
      storageLocation: entry.storageLocation || (idx >= 0 ? summaries[idx].storageLocation : ''),
      batchNumber: entry.batchNumber || (idx >= 0 ? summaries[idx].batchNumber : ''),
      expiryDate: entry.expiryDate || (idx >= 0 ? summaries[idx].expiryDate : null),
      facilityId: entry.facilityId,
      wardId: entry.wardId,
      hasVariance: entry.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY,
    };

    if (entry.transactionType === S8_TRANSACTION_TYPE.STOCK_CHECK || entry.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY) {
      summaryData.lastCountDate = entry.timestamp;
      summaryData.lastCountBalance = entry.balanceAfter;
      const next = new Date(entry.timestamp);
      next.setDate(next.getDate() + 1);
      summaryData.nextCountDue = next.toISOString();
    }

    if (idx >= 0) {
      const merged = createS8DrugSummary({ ...summaries[idx], ...summaryData });
      summaries[idx] = { ...merged, id: summaries[idx].id, updatedAt: new Date().toISOString() };
    } else {
      const newSummary = createS8DrugSummary(summaryData);
      newSummary.id = LocalStorageService.generateId();
      summaries.push(newSummary);
    }

    LocalStorageService.set(this.summaryKey, summaries);
  }
}

const s8RegisterService = new S8RegisterService();
export default s8RegisterService;
